import { useState } from "react";
import { motion } from "framer-motion";
import { ArrowUpRight } from "lucide-react";
import { toast } from "sonner";
import { SectionHeading, reveal } from "./SectionHeading";
import { PumpkinMark } from "./Logo";

const GHOSTS_IMG =
  "https://images.unsplash.com/photo-1698347188591-ee0181b4cc1d?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjA2ODl8MHwxfHNlYXJjaHw0fHxkYXJrJTIwc3Bvb2t5JTIwZ2hvc3QlMjBuaWdodHxlbnwwfHx8fDE3OTE4OTcxNDd8MA&ixlib=rb-4.1.0&q=85";

const fortunes = [
  "+1000x Ghost Multiplier bestowed upon you",
  "You escaped the Red Candle Ghoul",
  "Pumpkin Blessing: your bags are haunted with gains",
  "The Coven whispers: moon mission confirmed",
  "Trick! The chart dips... then rips through the stratosphere",
];

const XLogo = ({ size = 20 }) => (
  <svg width={size} height={size} viewBox="0 0 24 24" fill="currentColor" aria-hidden="true">
    <path d="M18.9 2.6h3.2l-7.1 8.1 8.3 11h-6.5l-5.1-6.7-5.9 6.7H2.6l7.6-8.7L2.2 2.6h6.7l4.6 6.1 5.4-6.1zm-1.1 17.1h1.8L8.1 4.5H6.2l11.6 15.2z" />
  </svg>
);

const DiscordLogo = ({ size = 20 }) => (
  <svg width={size} height={size} viewBox="0 0 24 24" fill="currentColor" aria-hidden="true">
    <path d="M20.317 4.37a19.79 19.79 0 00-4.885-1.515.074.074 0 00-.079.037c-.21.375-.444.864-.608 1.25a18.27 18.27 0 00-5.487 0 12.64 12.64 0 00-.617-1.25.077.077 0 00-.079-.037A19.736 19.736 0 003.677 4.37a.07.07 0 00-.032.027C.533 9.046-.32 13.58.099 18.058a.082.082 0 00.031.057 19.9 19.9 0 005.993 3.03.078.078 0 00.084-.028c.462-.63.874-1.295 1.226-1.994a.076.076 0 00-.041-.106 13.107 13.107 0 01-1.872-.892.077.077 0 01-.008-.128c.126-.094.252-.192.372-.291a.074.074 0 01.077-.01c3.928 1.793 8.18 1.793 12.062 0a.074.074 0 01.078.01c.12.098.246.198.373.292a.077.077 0 01-.006.127 12.3 12.3 0 01-1.873.892.077.077 0 00-.041.107c.36.698.772 1.363 1.225 1.993a.076.076 0 00.084.028 19.84 19.84 0 006.002-3.03.077.077 0 00.032-.054c.5-5.177-.838-9.674-3.549-13.66a.061.061 0 00-.031-.03zM8.02 15.33c-1.183 0-2.157-1.085-2.157-2.419 0-1.333.956-2.419 2.157-2.419 1.21 0 2.176 1.096 2.157 2.42 0 1.333-.956 2.418-2.157 2.418zm7.975 0c-1.183 0-2.157-1.085-2.157-2.419 0-1.333.955-2.419 2.157-2.419 1.21 0 2.176 1.096 2.157 2.42 0 1.333-.946 2.418-2.157 2.418z" />
  </svg>
);

export default function Community() {
  const [bursts, setBursts] = useState([]);

  const feed = () => {
    const id = Date.now();
    setBursts((b) => [...b, id]);
    toast.success(fortunes[Math.floor(Math.random() * fortunes.length)], {
      description: "The pumpkin has been fed. The coven grows stronger.",
    });
    setTimeout(() => setBursts((b) => b.filter((x) => x !== id)), 1100);
  };

  return (
    <section
      id="community"
      data-testid="community-section"
      className="relative overflow-hidden px-6 py-28 sm:px-12 sm:py-36"
    >
      <div className="pointer-events-none absolute left-1/2 top-0 h-[26rem] w-[40rem] -translate-x-1/2 rounded-full bg-phantom/15 blur-[140px]" />

      <div className="relative mx-auto max-w-7xl">
        <SectionHeading
          eyebrow="Final Chapter // The Dark Coven"
          title="Every Ghost Needs"
          accent="a Graveyard"
          description="The coven never sleeps. Plug into the official channels and catch the CA the second it materializes."
          testid="community-heading"
        />

        <div className="grid gap-6 md:grid-cols-2">
          <motion.article
            data-testid="community-card-x"
            {...reveal(0)}
            className="crypt-card flex min-h-[20rem] flex-col justify-between"
          >
            <div>
              <div className="flex items-center justify-between">
                <span className="flex h-12 w-12 items-center justify-center rounded-full border border-orange-500/40 bg-elevated text-zinc-100">
                  <XLogo size={20} />
                </span>
                <span className="rounded-full border border-orange-500/40 bg-pumpkin/10 px-3 py-1 font-mono text-[10px] font-semibold uppercase tracking-widest text-orange-300">
                  Official Channel
                </span>
              </div>
              <h3 className="mt-6 font-syne text-lg font-extrabold uppercase tracking-tight text-zinc-100 sm:text-xl">
                X / Twitter
              </h3>
              <p className="mt-1 font-mono text-xs tracking-widest text-orange-400/90">@FOMOWEENCOIN</p>
              <p className="mt-4 text-sm leading-relaxed text-zinc-400">
                Follow the official creator dispatch. Instant announcements,
                alpha drops, and Halloween meme wars — the CA reveal hits here
                first.
              </p>
            </div>
            <a
              data-testid="community-link-x"
              href="https://x.com/FOMOWEENCOIN"
              target="_blank"
              rel="noreferrer"
              className="mt-8 inline-flex w-fit items-center gap-2 rounded-full bg-pumpkin px-6 py-3 font-syne text-xs font-bold uppercase tracking-widest text-abyss transition-[transform,box-shadow] duration-300 hover:-translate-y-0.5 hover:shadow-[0_0_25px_rgba(255,107,0,0.5)]"
            >
              Enter the Graveyard <ArrowUpRight size={14} />
            </a>
          </motion.article>

          <motion.article
            data-testid="community-card-discord"
            {...reveal(0.12)}
            className="crypt-card flex min-h-[20rem] flex-col justify-between border-purple-500/25 hover:border-purple-400/60"
          >
            <img
              src={GHOSTS_IMG}
              alt=""
              aria-hidden="true"
              className="pointer-events-none absolute inset-0 h-full w-full object-cover opacity-20"
            />
            <div className="absolute inset-0 bg-gradient-to-t from-crypt via-crypt/80 to-crypt/40" />
            <div className="relative">
              <div className="flex items-center justify-between">
                <span className="flex h-12 w-12 items-center justify-center rounded-full border border-purple-500/40 bg-elevated text-purple-300">
                  <DiscordLogo size={20} />
                </span>
                <span className="rounded-full border border-purple-500/40 bg-purple-500/10 px-3 py-1 font-mono text-[10px] font-semibold uppercase tracking-widest text-purple-300">
                  Server Live
                </span>
              </div>
              <h3 className="mt-6 font-syne text-lg font-extrabold uppercase tracking-tight text-zinc-100 sm:text-xl">
                Discord Coven
              </h3>
              <p className="mt-1 font-mono text-xs tracking-widest text-purple-300/90">discord.gg/xSnUtaaBw</p>
              <p className="mt-4 text-sm leading-relaxed text-zinc-400">
                24/7 degen voice chat, ghost raids, real-time buy alerts, and
                seasonal hype rituals. The coven gathers here — step inside.
              </p>
            </div>
            <a
              data-testid="community-link-discord"
              href="https://discord.com/invite/xSnUtaaBw"
              target="_blank"
              rel="noreferrer"
              className="relative mt-8 inline-flex w-fit items-center gap-2 rounded-full border border-purple-500/50 bg-elevated px-6 py-3 font-syne text-xs font-bold uppercase tracking-widest text-purple-200 transition-colors duration-300 hover:border-purple-300 hover:text-white"
            >
              Join the Discord <ArrowUpRight size={14} />
            </a>
          </motion.article>
        </div>

        <motion.div {...reveal(0.2)} className="mt-16 text-center">
          <p className="font-mono text-[10px] font-semibold uppercase tracking-[0.3em] text-orange-400/80">
            Interactive Ritual
          </p>
          <div className="relative mt-6 inline-block">
            <motion.button
              data-testid="community-feed-pumpkin-btn"
              whileTap={{ scale: 0.92 }}
              onClick={feed}
              className="flex items-center gap-3 rounded-full border border-orange-500/40 bg-elevated px-8 py-4 font-syne text-sm font-bold uppercase tracking-widest text-zinc-100 transition-colors duration-300 hover:border-orange-400/80"
            >
              <PumpkinMark size={26} /> Feed the Pumpkin
            </motion.button>
            {bursts.map((id) => (
              <span key={id} className="pointer-events-none absolute inset-0">
                {Array.from({ length: 14 }).map((_, i) => (
                  <motion.span
                    key={i}
                    className="absolute left-1/2 top-1/2 h-2 w-2 rounded-full"
                    style={{
                      background:
                        i % 3 === 0 ? "#8B5CF6" : i % 3 === 1 ? "#FF6B00" : "#10B981",
                    }}
                    initial={{ x: 0, y: 0, opacity: 1, scale: 1 }}
                    animate={{
                      x: Math.cos((i / 14) * Math.PI * 2) * 120,
                      y: Math.sin((i / 14) * Math.PI * 2) * 120 - 40,
                      opacity: 0,
                      scale: 0.2,
                    }}
                    transition={{ duration: 1, ease: "easeOut" }}
                  />
                ))}
              </span>
            ))}
          </div>
          <p className="mt-4 text-xs text-zinc-500">
            Trick or treat. Feed it and receive a spooky fortune.
          </p>
        </motion.div>
      </div>
    </section>
  );
}
